import { useEffect } from 'react';
import type React from 'react';

import { useSearchParams } from 'react-router';

import { Translate } from '@change-corgi/core/react/i18n';
import { getWindowSafe } from '@change-corgi/core/window';

import { ENDORSEMENTS_EXPERIMENT_KEY, EndorsementSection } from 'src/app/shared/components/endorsements';
import { useSessionStorageValue } from 'src/app/shared/hooks/storage';
import { useTrackView } from 'src/app/shared/hooks/tracking';
import { useToast } from 'src/app/shared/toast';

import { PetitionContent } from './components/PetitionContent';
import { PetitionData } from './components/PetitionData';

export const EditForm: React.FC = () => {
	useTrackView('petition_edit_view');
	const currentWindow = getWindowSafe();
	const [searchParams, setSearchParams] = useSearchParams();
	const { showToast } = useToast();

	// Check for endorsementAdded or endorsementUpdated query params and show toast
	useEffect(() => {
		const endorsementAdded = searchParams.get('endorsementAdded');
		const endorsementUpdated = searchParams.get('endorsementUpdated');

		if (endorsementAdded === 'true' || endorsementUpdated === 'true') {
			// Remove the query param from URL
			const newParams = new URLSearchParams(searchParams);
			newParams.delete('endorsementAdded');
			newParams.delete('endorsementUpdated');
			setSearchParams(newParams, { replace: true });

			// Show success toast
			showToast({
				variant: 'success',
				duration: 5000,
				message:
					endorsementUpdated === 'true' ? (
						<Translate value="corgi.petition.starter_dashboard_v2.endorsements.success_toast.update_success_toast" />
					) : (
						<Translate value="corgi.petition.starter_dashboard_v2.endorsements.success_toast.message" />
					),
				action: {
					label: <Translate value="corgi.petition.starter_dashboard_v2.endorsements.success_toast.action_button" />,
					onClick: () => {
						// TODO: Navigate to petition page
						// eslint-disable-next-line no-console
						console.log('View on petition from toast clicked');
					},
				},
			});

			// Scroll to endorsements section after a delay to ensure content is rendered
			if (currentWindow) {
				const scrollToEndorsements = () => {
					const element = currentWindow.document.getElementById('endorsements');
					if (element) {
						const y = element.getBoundingClientRect().top + currentWindow.pageYOffset - 40;
						currentWindow.scrollTo({ top: y, behavior: 'smooth' });
					} else {
						// Retry if element not found (might not be rendered yet)
						setTimeout(scrollToEndorsements, 200);
					}
				};

				setTimeout(scrollToEndorsements, 100);
			}
		}
	}, [searchParams, setSearchParams, showToast, currentWindow]);

	// Read experiment value from sessionStorage (set by starterDashboardV2)
	const [storedValue] = useSessionStorageValue(ENDORSEMENTS_EXPERIMENT_KEY);
	const isEndorsementsEnabled = storedValue === 'enabled';

	// Handle URL hash scrolling on page load
	useEffect(() => {
		const handleHashScroll = () => {
			if (!currentWindow) return;

			const { hash } = currentWindow.location;
			if (hash) {
				const element = currentWindow.document.querySelector(hash);
				if (element) {
					// Add a small delay to ensure the page is fully rendered
					setTimeout(() => {
						const y = element.getBoundingClientRect().top + currentWindow.pageYOffset - 40; // 40px offset for better visibility
						currentWindow.scrollTo({ top: y, behavior: 'smooth' });
					}, 100);
				}
			}
		};

		// Handle initial hash on page load
		handleHashScroll();

		// Handle hash changes (if user navigates with hash)
		currentWindow?.addEventListener('hashchange', handleHashScroll);

		return () => {
			currentWindow?.removeEventListener('hashchange', handleHashScroll);
		};
	}, [currentWindow]);

	return (
		<>
			<PetitionContent />
			{isEndorsementsEnabled && (
				<div className="mt-10">
					<EndorsementSection />
				</div>
			)}
			<PetitionData />
		</>
	);
};
